import React, { useState } from 'react';

import { v4 as uuidv4 } from 'uuid';

import { Button, Paper, Stack, Tab, Tabs, TextField, Typography } from '@mui/material';

import { EmployeeUserOption } from '~/hooks/apis/users/useGetEmployeeUsers';

import { FeedMention } from '~/types/contracting/feed';

import ChatterEditor from './ChatterEditor';
import { CHATTER_EDITOR_PLACEHOLDER } from './constants';
import { ChatterDraft } from './types';
import { addMention, draftLimitMessage, mentionsInText } from './utils';

interface Props {
  scopeLabel: string;
  isSending: boolean;
  // Resolves true once the post is queued, so the draft can be cleared.
  onShare: (draft: ChatterDraft) => Promise<boolean>;
}

export default function ChatterComposer({ scopeLabel, isSending, onShare }: Props) {
  const [text, setText] = useState('');
  const [mentions, setMentions] = useState<Array<FeedMention>>([]);
  const [isExpanded, setIsExpanded] = useState(false);
  // Bumped after sharing to remount (clear) the uncontrolled editor.
  const [editorKey, setEditorKey] = useState(0);
  const [idempotencyKey, setIdempotencyKey] = useState(() => uuidv4());
  const draftMentions = mentionsInText(mentions, text);
  const limitMessage = draftLimitMessage(text, draftMentions);

  const resetDraft = () => {
    setText('');
    setMentions([]);
    setEditorKey((prev) => prev + 1);
    setIdempotencyKey(uuidv4());
    setIsExpanded(false);
  };

  const handleShare = async () => {
    if (await onShare({ text, mentions: draftMentions, idempotencyKey })) {
      resetDraft();
    }
  };

  const handleMention = (employee: EmployeeUserOption) => setMentions((prev) => addMention(prev, employee));

  return (
    <Paper variant="outlined">
      <Tabs value="post" sx={{ px: 1 }}>
        <Tab value="post" label="Post" />
      </Tabs>
      {!isExpanded ? (
        <Stack direction="row" spacing={1} p={2}>
          <TextField
            fullWidth
            size="small"
            placeholder={CHATTER_EDITOR_PLACEHOLDER}
            onClick={() => setIsExpanded(true)}
            onFocus={() => setIsExpanded(true)}
            slotProps={{
              htmlInput: { 'aria-label': CHATTER_EDITOR_PLACEHOLDER, style: { textAlign: 'center' } },
            }}
          />
          <Button variant="contained" color="primary" disabled>
            Share
          </Button>
        </Stack>
      ) : (
        <Stack spacing={2} p={2}>
          <ChatterEditor
            key={editorKey}
            placeholder={CHATTER_EDITOR_PLACEHOLDER}
            onChange={setText}
            onMention={handleMention}
            autoFocus
          />

          <Stack direction="row" alignItems="center" justifyContent="flex-end" spacing={2}>
            <Typography variant="caption" color={limitMessage ? 'error' : 'text.secondary'}>
              {limitMessage ?? `Posts to this ${scopeLabel}'s Salesforce feed.`}
            </Typography>
            <Button variant="text" onClick={resetDraft} disabled={isSending}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              disabled={!text || !!limitMessage || isSending}
              onClick={handleShare}
            >
              {isSending ? 'Sending…' : 'Share'}
            </Button>
          </Stack>
        </Stack>
      )}
    </Paper>
  );
}
